import { useState } from 'react';
import { ArrowUp, X } from 'lucide-react';
import { useLocale } from '../context/LocaleContext';

interface SearchInputProps {
  placeholder?: string;
  onSubmit?: (value: string, selectedOptions: string[]) => void;
  variant?: 'primary' | 'secondary';
  selectedOptions: string[];
  onRemoveOption: (option: string) => void;
  disabled?: boolean;
  staticPrefix?: string; // non-editable prefix shown before user input; not sent to backend
}

export function SearchInput({ 
  placeholder,
  onSubmit,
  variant = 'primary',
  selectedOptions,
  onRemoveOption,
  disabled = false,
  staticPrefix,
}: SearchInputProps) {
  const { t } = useLocale();
  const [value, setValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (disabled) return;
    if (onSubmit) {
      onSubmit(value, selectedOptions);
      // Optionally clear the input after submit
      setValue('');
    }
  };

  return (
    <div className="w-full">
      <form 
        onSubmit={handleSubmit} 
        className="relative w-full flex items-center"
      >
        <div className="w-full px-[24px] py-[16px] pr-[64px] rounded-full bg-white border border-gray-200 text-foreground shadow-md focus-within:border-primary flex items-center gap-[8px] flex-wrap">
          {staticPrefix && (
            <div 
              className="flex items-center gap-[8px] px-[16px] py-[4px] rounded-md border border-gray-200 text-[16px] bg-white"
              aria-label="Static input prefix"
            >
              {staticPrefix}
            </div>
          )}
          {selectedOptions.map(option => (
            <div 
              key={option} 
              className="flex items-center gap-1 px-3 py-1 rounded-md border border-green-200 text-sm"
            >
              {option}
              <button
                type="button"
                onClick={() => onRemoveOption(option)}
                className="ml-1 flex items-center justify-center w-4 h-4 rounded-full hover:bg-green-100"
                aria-label={`Remove ${option}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={(selectedOptions.length > 0 || !!staticPrefix) ? "" : (placeholder ?? t("chat.placeholder"))}
            className="flex-1 bg-transparent h-[36px] focus:outline-none text-[16px] min-w-[50px] placeholder:text-gray-400"
            style={{
              fontFamily: 'var(--font-body)',
            }}
            disabled={disabled}
            readOnly={disabled}
          />
        </div>
        <button 
          type="submit"
          disabled={disabled}
          className="absolute right-[12px] p-[8px] rounded-full flex items-center justify-center ww-btn-primary"
          aria-label="Submit"
          style={{
            opacity: disabled ? 0.5 : 1,
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        >
          <ArrowUp className="w-[24px] h-[24px]" style={{ color: 'inherit' }} />
        </button>
      </form>
    </div>
  );
} 